import { Assets } from 'pixi.js';

import { Physics, Spine, AtlasAttachmentLoader, SkeletonJson } from '@esotericsoftware/spine-pixi-v8';

import { gameConfig } from '../config/gameConfig.js';
import { loadFireWinSpriteFrames } from './fireSpriteLoader.js';
import { preloadWildMultiplierTextures } from './wildMultiplierBadge.js';



/** Live symbol folder. Buy bonus passes `dir: '/assets/spines/bb_symbols'`. */

const DEFAULT_SPINE_DIR = '/assets/spines';



/** Max fraction of cell (per axis) used for fitting; one scale shared by every symbol. */

const SPINE_CELL_INSET = 1.1;



/** Uniform scale for all reel symbols; set in {@link loadSpineAssets}. */

let _globalSymbolSpineScale = 1;



/**

 * @param {import('@esotericsoftware/spine-core').SkeletonData} skeletonData

 * @param {import('@esotericsoftware/spine-pixi-v8').TextureAtlas} textureAtlas

 * @returns {{ bw: number, bh: number }}

 */

function _measureSpineSetupSize(skeletonData, textureAtlas) {

  const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);

  const skeletonJson = new SkeletonJson(attachmentLoader);

  const skeleton = skeletonJson.readSkeletonData(skeletonData);

  const spine = new Spine(skeleton);

  spine.scale.set(1);

  spine.skeleton.setToSetupPose();

  spine.skeleton.updateWorldTransform(Physics.update);



  const b0 = spine.getBounds();

  const data = spine.skeleton.data;

  const bw = Math.max(

    1,

    b0.width,

    typeof data.width === 'number' && data.width > 0 ? data.width : 0,

  );

  const bh = Math.max(

    1,

    b0.height,

    typeof data.height === 'number' && data.height > 0 ? data.height : 0,

  );



  spine.destroy({ children: true });

  return { bw, bh };

}



/**

 * One scale for all symbols: tightest fit so every skeleton still fits inside the cell.

 * @param {Record<number, { spineName: string, skeletonData: unknown, textureAtlas: unknown }>} spineData

 */

function _computeGlobalSymbolSpineScale(spineData) {

  const tw = gameConfig.cellWidth * SPINE_CELL_INSET;

  const th = gameConfig.cellHeight * SPINE_CELL_INSET;

  const seen = new Set();

  let scale = Infinity;



  for (const key of Object.keys(spineData)) {

    const row = spineData[key];

    if (!row?.skeletonData || !row.textureAtlas) continue;

    const name = row.spineName;

    if (seen.has(name)) continue;

    seen.add(name);



    try {

      const { bw, bh } = _measureSpineSetupSize(row.skeletonData, row.textureAtlas);

      const s = Math.min(tw / bw, th / bh);

      scale = Math.min(scale, s);

    } catch {

      /* skip broken skeleton */

    }

  }



  _globalSymbolSpineScale = Number.isFinite(scale) && scale > 0 ? scale : 1;

}



/**

 * Loads all Spine assets for symbols

 * @param {{ onProgress?: (t: number) => void, dir?: string, preloadWildMultipliers?: boolean }} [opts]

 * @returns {Promise<Record<number, any>>}

 */

export async function loadSpineAssets(opts = {}) {

  const { onProgress } = opts;
  const spineDir = String(opts.dir || DEFAULT_SPINE_DIR).replace(/\/$/, '');

  const spineData = {};



  const spineFiles = new Set();

  for (const symbol of gameConfig.symbols) {

    if (symbol.spine) {

      spineFiles.add(symbol.spine);

    }

  }



  const totalSteps = 1 + spineFiles.size + 1 + 2 + 2;

  let doneSteps = 0;

  const bump = () => {

    doneSteps += 1;

    if (typeof onProgress === 'function') {

      onProgress(Math.min(1, doneSteps / totalSteps));

    }

  };



  let textureAtlas;

  try {

    // spine-pixi-v8 registers a loader: fetches .atlas, parses pages, loads each page image

    // from the same directory (e.g. slots.png — must exist beside slots.atlas).

    textureAtlas = await Assets.load(`${spineDir}/slots.atlas`);

  } catch (error) {

    console.error(

      'Failed to load Spine texture atlas. Ensure slots.atlas and its page image (e.g. slots.png) exist under /assets/spines/.',

      error,

    );

    return spineData;

  }

  bump();



  const loadPromises = Array.from(spineFiles).map(async (spineName) => {

    try {

      const response = await fetch(`${spineDir}/${spineName}.json`);

      const skeletonDataJson = await response.json();

      bump();

      return { spineName, skeletonData: skeletonDataJson, textureAtlas };

    } catch (error) {

      console.error(`Failed to load spine asset: ${spineName}`, error);

      bump();

      return null;

    }

  });



  const results = await Promise.all(loadPromises);



  for (const symbol of gameConfig.symbols) {

    const result = results.find((r) => r && r.spineName === symbol.spine);

    if (result) {

      spineData[symbol.id] = result;

    }

  }



  _computeGlobalSymbolSpineScale(spineData);

  bump();



  try {

    const winAtlas = await Assets.load('/assets/spines/win_animations/skeleton.atlas');

    bump();

    const response = await fetch('/assets/spines/win_animations/skeleton.json');

    if (!response.ok) {

      throw new Error(`HTTP ${response.status}`);

    }

    const skeletonDataJson = await response.json();

    bump();

    spineData.winCelebration = {

      spineName: 'winCelebration',

      skeletonData: skeletonDataJson,

      textureAtlas: winAtlas,

    };

  } catch (error) {

    console.warn('Failed to load win celebration spine (win_animations):', error);

    bump();

    bump();

  }



  try {
    const expandingWildRes = await fetch(`${spineDir}/pharaon_extanded.json`);
    if (!expandingWildRes.ok) {
      throw new Error(`HTTP ${expandingWildRes.status}`);
    }
    const expandingWildSkeleton = await expandingWildRes.json();
    bump();
    spineData.expandingWildSpine = {
      spineName: 'pharaon_extanded',
      skeletonData: expandingWildSkeleton,
      textureAtlas,
    };
  } catch (error) {
    console.warn('Failed to load expanding wild spine (pharaon_extanded):', error);
    bump();
  }



  try {
    spineData.fireWinFrames = await loadFireWinSpriteFrames();
    bump();
  } catch (error) {
    console.warn('Failed to load fire win spritesheet:', error);
    spineData.fireWinFrames = [];
    bump();
  }

  if (opts.preloadWildMultipliers) {
    try {
      await preloadWildMultiplierTextures();
    } catch (error) {
      console.warn('Failed to load wild multiplier badges:', error);
    }
  }

  if (typeof onProgress === 'function') {

    onProgress(1);

  }



  return spineData;

}



/**

 * Creates a Spine instance from loaded data

 * @param {number} symbolId

 * @param {Record<number, any>} spineAssets

 * @returns {Spine | null}

 */

export function createSpineInstance(symbolId, spineAssets) {

  const spineResource = spineAssets[symbolId];

  if (!spineResource) {

    console.warn(`No spine data for symbol ${symbolId}`);

    return null;

  }



  try {

    const { skeletonData, textureAtlas } = spineResource;



    const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);

    const skeletonJson = new SkeletonJson(attachmentLoader);



    const skeleton = skeletonJson.readSkeletonData(skeletonData);



    const spine = new Spine(skeleton);



    if (spine && spine.skeleton) {

      spine.skeleton.setToSetupPose();

      spine.skeleton.updateWorldTransform(Physics.update);



      spine.scale.set(_globalSymbolSpineScale);

      spine.skeleton.updateWorldTransform(Physics.update);



      const b = spine.getBounds();

      spine.x = -(b.x + b.width * 0.5);

      spine.y = -(b.y + b.height * 0.5);



      if (spine.state) {

        try {

          const idleName =

            gameConfig.animations?.idle && spine.skeleton.data.findAnimation(gameConfig.animations.idle)

              ? gameConfig.animations.idle

              : spine.skeleton.data.findAnimation('idle')

                ? 'idle'

                : null;

          if (idleName) {

            spine.state.setAnimation(0, idleName, true);

          }

        } catch (e) {

          console.warn('Could not set initial idle animation:', e);

        }

      }



      return spine;

    }

  } catch (error) {

    console.error(`Failed to create spine instance for symbol ${symbolId}:`, error);

  }



  return null;

}



/**

 * Decorative frame around a winning cell (uses {@link spineData.winFrame} from {@link loadSpineAssets}).

 * @param {Record<string | number, any>} spineAssets

 * @returns {import('@esotericsoftware/spine-pixi-v8').Spine | null}

 */

let _loggedWinFrameError = false;



export function createWinFrameSpineInstance(spineAssets) {

  const spineResource = spineAssets?.winFrame;

  if (!spineResource?.skeletonData || !spineResource?.textureAtlas) {

    return null;

  }



  try {

    const { skeletonData, textureAtlas } = spineResource;

    const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);

    const skeletonJson = new SkeletonJson(attachmentLoader);

    const skeleton = skeletonJson.readSkeletonData(skeletonData);

    const spine = new Spine(skeleton);

    if (!spine?.skeleton) return null;



    spine.skeleton.setToSetupPose();

    spine.skeleton.updateWorldTransform(Physics.update);

    spine.scale.set(_globalSymbolSpineScale);

    spine.skeleton.updateWorldTransform(Physics.update);



    const b = spine.getBounds();

    spine.x = -(b.x + b.width * 0.5);

    spine.y = -(b.y + b.height * 0.5);



    spine.visible = false;

    return spine;

  } catch (error) {

    if (!_loggedWinFrameError) {

      _loggedWinFrameError = true;

      console.error('Failed to create win frame spine:', error);

    }

    return null;

  }

}



/**
 * Full-column expanding-wild spine (`pharaon_extanded.json`).
 * @param {Record<string | number, any>} spineAssets
 * @returns {import('@esotericsoftware/spine-pixi-v8').Spine | null}
 */
export function createExpandingWildSpineInstance(spineAssets) {
  const spineResource = spineAssets?.expandingWildSpine;
  if (!spineResource?.skeletonData || !spineResource?.textureAtlas) {
    return null;
  }

  try {
    const { skeletonData, textureAtlas } = spineResource;
    const attachmentLoader = new AtlasAttachmentLoader(textureAtlas);
    const skeletonJson = new SkeletonJson(attachmentLoader);
    const skeleton = skeletonJson.readSkeletonData(skeletonData);
    const spine = new Spine(skeleton);
    if (!spine?.skeleton) return null;

    spine.skeleton.setToSetupPose();
    spine.skeleton.updateWorldTransform(Physics.update);

    const cfg = gameConfig.adapter;
    const blockH = gameConfig.rowCount * gameConfig.cellHeight;
    const pad = cfg.expandingWildScalePad ?? 1.12;
    const targetH = blockH * pad;

    const b = spine.getBounds();
    const fitScale = b.height > 0 ? targetH / b.height : 1;
    spine.scale.set(fitScale);
    spine.skeleton.updateWorldTransform(Physics.update);

    const fitB = spine.getBounds();
    spine.x = -(fitB.x + fitB.width * 0.5);
    spine.y = -(fitB.y + fitB.height * 0.5);
    spine.eventMode = 'none';
    return spine;
  } catch (error) {
    console.error('Failed to create expanding wild spine:', error);
    return null;
  }
}

/**
 * Visible width of a multiplier region attachment, in screen pixels.
 * Uses the trimmed atlas region so transparent padding is not counted.
 * @param {{ width: number, scaleX: number, region?: { originalWidth?: number, width?: number, height?: number, degrees?: number } | null }} att
 * @param {number} boneScale
 * @param {number} displayScale
 */
function _expandingWildMultiplierScreenWidth(att, boneScale, displayScale) {
  const region = att.region;
  let localW = Math.abs(att.width * att.scaleX);
  if (region && region.originalWidth > 0) {
    const regionScaleX = (att.width / region.originalWidth) * att.scaleX;
    const rotated = region.degrees === 90 || region.degrees === 270;
    const span = rotated ? region.height : region.width;
    if (span > 0) localW = Math.abs(span * regionScaleX);
  }
  return localW * boneScale * displayScale;
}

/**
 * The buy-bonus multiplier is drawn on the expanding-wild spine and, once the
 * column is fitted to the reel, comes out wider than the reel. Scale those
 * attachments so the graphic sits a little inside the reel width.
 * Call after the spine's horizontal scale is final.
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine | null | undefined} spine
 */
export function fitExpandingWildMultiplierToReel(spine) {
  if (!spine?.skeleton?.data || spine._wildMultiplierFitted) return;
  const data = spine.skeleton.data;
  const slot = data.findSlot('multipliers');
  const byName = data.defaultSkin?.attachments?.[slot?.index];
  if (!slot || !byName) return;

  const ratio = Number(gameConfig.adapter.expandingWildMultiplierWidthRatio);
  const targetW = gameConfig.cellWidth * (Number.isFinite(ratio) && ratio > 0 ? ratio : 0.92);
  const bone = spine.skeleton.findBone(slot.boneData.name);
  let boneScale = bone && typeof bone.getWorldScaleX === 'function'
    ? Math.abs(bone.getWorldScaleX())
    : 0;
  if (!(boneScale > 0)) boneScale = Math.abs(bone?.scaleX) || 1;
  const displayScale = Math.abs(spine.scale?.x) || 1;

  for (const att of Object.values(byName)) {
    if (!att || typeof att.updateRegion !== 'function' || !(att.width > 0)) continue;
    const screenW = _expandingWildMultiplierScreenWidth(att, boneScale, displayScale);
    if (!(screenW > targetW)) continue;
    const factor = targetW / screenW;
    att.scaleX *= factor;
    att.scaleY *= factor;
    att.updateRegion();
  }
  spine._wildMultiplierFitted = true;
  spine.skeleton.updateWorldTransform(Physics.update);
}

/**
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
 */
export function resetExpandingWildSpine(spine) {
  if (!spine?.skeleton) return;
  spine.state?.clearTracks();
  spine.skeleton.setToSetupPose();
  spine.skeleton.updateWorldTransform(Physics.update);
}

/**
 * Buy-bonus expanding wild (`pharaon_extanded`): `idle2x`/`win2x`, `idle3x`/`win3x`, `idle5x`/`win5x`, `idle10x`/`win10x`.
 * Multipliers 2/3/5/10 use that clip. Anything else stays on `win`, which clears the default 10x graphic.
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine | null | undefined} spine
 * @param {number} [multiplier]
 * @returns {string}
 */
export function resolveExpandingWildSpineAnimation(spine, multiplier = 0) {
  const fallback = gameConfig.adapter.expandingWildSpineAnimation ?? 'win';
  const n = Math.round(Number(multiplier));
  const data = spine?.skeleton?.data;
  /** `idle` / setup pose leaves the 10x attachment on. Only an exact 2/3/5/10 clip may replace it. */
  const names = n === 2 || n === 3 || n === 5 || n === 10 ? [`idle${n}x`, `win${n}x`] : [];
  for (const name of names) {
    if (data?.findAnimation?.(name)) return name;
  }
  return data?.findAnimation?.(fallback) ? fallback : 'win';
}

/**
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
 * @param {string} [animName]
 * @param {number} [timeScale]
 * @returns {number} animation duration in seconds
 */
export function playExpandingWildSpineAnimation(spine, animName = 'win', timeScale = 1) {
  if (!spine?.state || !spine?.skeleton?.data) return 0;
  const resolved =
    spine.skeleton.data.findAnimation(animName)
      ? animName
      : (gameConfig.adapter.expandingWildSpineAnimation ?? 'win');
  const anim = spine.skeleton.data.findAnimation(resolved);
  if (!anim) return 0;
  animName = resolved;
  const scale = Math.max(0.01, timeScale);
  const loop = animName.startsWith('idle') || (anim.duration ?? 0) <= 0;
  spine.state.timeScale = 3;
  const entry = spine.state.setAnimation(0, animName, loop);
  if (entry) {
    entry.mixDuration = 0;
    entry.timeScale = scale;
  }
  // Frame 0 now — setup pose is the 10x graphic, so the first paint must already be the chosen clip.
  spine.state.update(0);
  spine.skeleton.updateWorldTransform(Physics.update);
  return (anim.duration ?? 0) / scale;
}

/**
 * Freeze expanding-wild spine on its last frame (screenshot / debug hold).
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
 * @param {string} [animName]
 * @returns {number} animation duration in seconds
 */
export function holdExpandingWildSpineAtEnd(spine, animName = 'win') {
  if (!spine?.state || !spine?.skeleton?.data) return 0;
  const anim = spine.skeleton.data.findAnimation(animName);
  if (!anim) return 0;
  spine.state.clearTracks();
  spine.skeleton.setToSetupPose();
  spine.skeleton.updateWorldTransform(Physics.update);
  const entry = spine.state.setAnimation(0, animName, false);
  const duration = anim.duration ?? 0;
  if (entry) {
    entry.mixDuration = 0;
    entry.trackTime = Math.max(0, duration - 1 / 60);
    entry.timeScale = 0;
  }
  spine.state.update(0);
  spine.skeleton.updateWorldTransform(Physics.update);
  return duration;
}


